// AddInventory.jsx — Warehouse portal: Add Inventory tool
// Scan barcodes to log received stock, then submit for manager review + apply.
import React, {
    useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getPresserSession, clearPressingSession,
    pressingFetch, authenticatedFetch, useAuth,
} from './AuthComponent';
import { useStore } from './StoreContext';
import useConsoleLocations, { WAREHOUSE_V2 } from './useConsoleLocations';
import InvQtyActions from './InvQtyActions';
import { useScanAlert, useScannerCapture } from './InvScanKit';
import { hasRole } from './constants/roles';
import { exportReviewCsv } from './invExport';

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
    shell:       { minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column', position: 'relative' },
    bar:         { background: '#1e3a5f', padding: 'calc(16px + env(safe-area-inset-top, 0px)) 16px 12px', color: '#fff', flexShrink: 0 },
    barRow:      { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    barTitle:    { fontSize: 20, fontWeight: 800, letterSpacing: 1 },
    barSub:      { fontSize: 12, color: '#93c5fd', marginTop: 2 },
    backBtn:     { background: 'none', border: 'none', color: '#93c5fd', fontSize: 13,
                   cursor: 'pointer', padding: '4px 0', marginTop: 4 },
    body:        { flex: 1, overflow: 'auto', padding: '0 0 80px' },
    // scan input
    scanCard:    { background: '#fff', margin: '12px 12px 0', borderRadius: 10,
                   padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    scanLabel:   { fontSize: 12, color: '#6b7280', marginBottom: 6, fontWeight: 600 },
    scanRow:     { display: 'flex', gap: 8 },
    scanInput:   { flex: 1, fontSize: 16, padding: '10px 12px', border: '2px solid #3b82f6',
                   borderRadius: 8, outline: 'none', background: '#f0f9ff' },
    scanBtn:     { padding: '10px 18px', background: '#1e3a5f', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' },
    scanBtnBusy: { background: '#6b7280' },
    flashCard:   { margin: '8px 12px 0', background: '#d1fae5', borderRadius: 8,
                   padding: '10px 14px', fontSize: 13, fontWeight: 600, color: '#065f46' },
    errCard:     { margin: '8px 12px 0', background: '#fee2e2', borderRadius: 8,
                   padding: '10px 14px', fontSize: 13, color: '#991b1b' },
    // line list
    lineSection: { margin: '12px 12px 0' },
    lineHdr:     { fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 6,
                   textTransform: 'uppercase', letterSpacing: .5 },
    lineCard:    { background: '#fff', borderRadius: 10, padding: '10px 12px',
                   marginBottom: 8, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
                   display: 'flex', alignItems: 'center', gap: 10 },
    lineQty:     { minWidth: 36, height: 36, borderRadius: 8, background: '#1e3a5f',
                   color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                   fontSize: 18, fontWeight: 800, flexShrink: 0 },
    lineInfo:    { flex: 1, overflow: 'hidden' },
    lineSku:     { fontSize: 13, fontWeight: 700, color: '#111827',
                   overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    lineTitle:   { fontSize: 11, color: '#9ca3af', overflow: 'hidden',
                   textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    undoBtn:     { padding: '6px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb',
                   borderRadius: 6, cursor: 'pointer', fontSize: 13, color: '#6b7280',
                   flexShrink: 0 },
    // bottom bar
    bottomBar:   { position: 'sticky', bottom: 0,
                   background: '#fff', borderTop: '1px solid #e5e7eb',
                   padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                   boxShadow: '0 -2px 8px rgba(0,0,0,.06)' },
    countPill:   { fontSize: 13, color: '#6b7280', fontWeight: 600 },
    submitBtn:   { padding: '11px 28px', background: '#059669', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    submitBtnDis:{ background: '#9ca3af', cursor: 'not-allowed' },
    // review screen
    revTable:    { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    revTh:       { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb',
                   fontWeight: 700, color: '#374151', fontSize: 12 },
    revTd:       { padding: '8px 10px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
    revCard:     { background: '#fff', margin: '12px 12px 0', borderRadius: 10,
                   overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.07)' },
    applyBtn:    { padding: '12px 32px', background: '#1e3a5f', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    applyBtnDis: { background: '#9ca3af', cursor: 'not-allowed' },
    // success screen
    successWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center',
                   padding: '48px 24px', gap: 12, textAlign: 'center' },
    successIcon: { fontSize: 56 },
    successH:    { fontSize: 22, fontWeight: 800, color: '#111827' },
    successSub:  { fontSize: 14, color: '#6b7280', maxWidth: 260 },
    doneBtn:     { marginTop: 16, padding: '12px 32px', background: '#1e3a5f', color: '#fff',
                   border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
    // location picker sheet
    overlay:     { position: 'absolute', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 600,
                   display: 'flex', alignItems: 'flex-end' },
    sheet:       { background: '#fff', borderRadius: '16px 16px 0 0', padding: '20px 16px 32px',
                   width: '100%', maxHeight: '60vh', overflowY: 'auto' },
    sheetTitle:  { fontSize: 17, fontWeight: 700, marginBottom: 14, color: '#111827' },
    locBtn:      { width: '100%', textAlign: 'left', padding: '12px 14px', margin: '4px 0',
                   border: '1px solid #e5e7eb', borderRadius: 8, background: '#fff',
                   fontSize: 14, cursor: 'pointer', fontWeight: 600, color: '#1e3a5f' },
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function AddInventory({ consoleMode = false }) {
    const navigate   = useNavigate();
    const { user }   = useAuth();
    const { activeStore } = useStore();
    const consoleLocs = useConsoleLocations(consoleMode);   // null while loading; already scoped to the user and the sidebar location
    const [session]  = useState(() => consoleMode ? null : getPresserSession());

    // Location state
    const [locationId,   setLocationId]   = useState(null);
    const [locationName, setLocationName] = useState('');
    const [storeKey,     setStoreKey]     = useState(null);
    const [showLocPicker, setShowLocPicker] = useState(false);

    // Session state
    const [sessionId,  setSessionId]  = useState(null);
    const [lastItem,   setLastItem]   = useState(null);   // {sku,title}: the item just scanned, for Set / Add quantity
    const [qtyBusy,    setQtyBusy]    = useState(false);
    const [screen,     setScreen]     = useState('scan'); // 'scan'|'review'|'applied'
    const [lines,      setLines]      = useState([]);     // client-side in scan; server data in review
    const [scanInput,  setScanInput]  = useState('');
    const [scanning,   setScanning]   = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [applying,   setApplying]   = useState(false);
    const [scanError,  setScanError]  = useState(null);
    const [lastFlash,  setLastFlash]  = useState(null);  // {sku, title}
    const [applyResult, setApplyResult] = useState(null);
    const [breakdown,  setBreakdown]  = useState(null);   // Review: {skus:{sku:{users,adjustment}}, unmatched}
    const [openSkus,   setOpenSkus]   = useState({});     // Review: which SKU rows are expanded
    const [frozen, setFrozen] = useState(false);              // resumed a session that was already submitted (v2: Back to scanning reopens it)
    const [reviewLines, setReviewLines] = useState([]);   // v2 review: server lines with live on-hand; the scan list stays untouched

    const scanRef   = useRef(null);
    const { alertMsg, raise: raiseScanAlert, alertNode } = useScanAlert(scanRef);   // invalid scan: red flash + buzz + tap-to-dismiss notice
    const flashTimer = useRef(null);

    // ── Auth / location setup ─────────────────────────────────────────────
    useEffect(() => {
        if (consoleMode) {
            if (!user) navigate('/login', { replace: true });
        } else {
            if (!session)                              { navigate('/pressing-portal/login', { replace: true }); return; }
            if (!session.roles?.includes('warehouse')) { navigate('/pressing-portal/login', { replace: true }); return; }
        }
    }, [consoleMode, user, session, navigate]);

    const myAssignments = useMemo(() => {
        if (!consoleMode) return session?.assignments || [];
        if (!Array.isArray(consoleLocs)) return [];
        return consoleLocs.map(l => ({ shopify_location_id: l.shopify_location_id, location_name: l.name, store_key: activeStore }));
    }, [consoleMode, session, consoleLocs, activeStore]);

    useEffect(() => {
        if (consoleMode && consoleLocs === null) return;
        if (myAssignments.length === 1 && !locationId) {
            setLocationId(myAssignments[0].shopify_location_id);
            setLocationName(myAssignments[0].location_name || '');
            setStoreKey(myAssignments[0].store_key);
        } else if (myAssignments.length > 1 && !locationId) {
            setShowLocPicker(true);
        }
    }, [myAssignments, locationId, consoleMode, consoleLocs]);

    // The sidebar location (or the user's access) changed under us: drop a location that is no longer offered.
    useEffect(() => {
        if (!consoleMode || consoleLocs === null || !locationId) return;
        if (myAssignments.some(a => String(a.shopify_location_id) === String(locationId))) return;
        setLocationId(null); setLocationName(''); setStoreKey(null);
        setSessionId(null); setLines([]); setLastItem(null); setFrozen(false); setScreen('scan'); setShowLocPicker(false);
    }, [consoleMode, consoleLocs, myAssignments, locationId]);

    // ── Fetch helper (kiosk vs console) ──────────────────────────────────
    const invFetch = useMemo(() => {
        if (consoleMode) return (path, opts = {}) => authenticatedFetch(path, opts);
        const pid = session?.presser_id;
        return (path, opts = {}) => {
            const sep = path.includes('?') ? '&' : '?';
            return pressingFetch(`${path}${sep}presser_id=${encodeURIComponent(pid)}`, opts);
        };
    }, [consoleMode, session]);

    // Review: who scanned or entered what quantity for each SKU total.
    useEffect(() => {
        if (screen !== 'review' || !sessionId || !storeKey || !locationId) return;
        let live = true;
        invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/scans?shopify_location_id=${encodeURIComponent(locationId)}`)
            .then(r => r.ok ? r.json() : null)
            .then(j => { if (live && j) setBreakdown(j); })
            .catch(() => {});
        return () => { live = false; };
    }, [screen, sessionId, storeKey, locationId, invFetch]);


    // ── Session management ────────────────────────────────────────────────
    // On location set, check for an existing open/submitted session to resume.
    useEffect(() => {
        if (!locationId || !storeKey) return;
        let live = true;
        invFetch(`/api/inv/${storeKey}/sessions?shopify_location_id=${encodeURIComponent(locationId)}&session_type=add_inventory`)
            .then(r => r.ok ? r.json() : { sessions: [] })
            .then(j => {
                if (!live) return;
                const open = (j.sessions || []).find(s => s.status === 'open');
                const submitted = (j.sessions || []).find(s => s.status === 'submitted');
                if (submitted) {
                    setSessionId(submitted.id);
                    // Resume review screen: fetch lines
                    invFetch(`/api/inv/${storeKey}/sessions/${submitted.id}?shopify_location_id=${encodeURIComponent(locationId)}`)
                        .then(r2 => r2.ok ? r2.json() : null)
                        .then(j2 => {
                            if (!live || !j2) return;
                            if (WAREHOUSE_V2) {
                                setReviewLines(j2.lines || []);
                                // the scan list too, so Back to scanning shows what was already scanned
                                setLines((j2.lines || []).map(l => ({ sku: l.sku, title: null, scanned: l.scanned })));
                                setFrozen(true);
                            } else setLines(j2.lines || []);
                            setScreen('review');
                        });
                } else if (open) {
                    setSessionId(open.id);
                    invFetch(`/api/inv/${storeKey}/sessions/${open.id}?shopify_location_id=${encodeURIComponent(locationId)}`)
                        .then(r2 => r2.ok ? r2.json() : null)
                        .then(j2 => {
                            if (!live || !j2) return;
                            // Rebuild client-side lines from DB lines
                            setLines((j2.lines || []).map(l => ({
                                sku:     l.sku,
                                title:   null,
                                scanned: l.scanned,
                            })));
                        });
                }
            })
            .catch(() => {});
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, storeKey]);

    const ensureSession = useCallback(async () => {
        if (sessionId) return sessionId;
        const r = await invFetch(`/api/inv/${storeKey}/sessions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ shopify_location_id: locationId, session_type: 'add_inventory' }),
        });
        if (!r.ok) throw new Error('Failed to create session');
        const j = await r.json();
        setSessionId(j.session_id);
        return j.session_id;
    }, [sessionId, storeKey, locationId, invFetch]);

    // ── Scan handler ─────────────────────────────────────────────────────
    const handleScan = useCallback(async (code) => {
        const barcode = (typeof code === 'string' ? code : scanInput).trim();   // a captured scanner code, or what was typed
        if (!barcode || !locationId || !storeKey || scanning) return;
        setScanInput('');
        setScanning(true);
        setScanError(null);
        clearTimeout(flashTimer.current);
        try {
            const sid = await ensureSession();
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sid}/scan?shopify_location_id=${encodeURIComponent(locationId)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ barcode, qty: 1 }),
                },
            );
            const j = await r.json();
            if (!r.ok) { raiseScanAlert(j.detail || 'Scan failed'); return; }

            if (j.matched) {
                setLines(prev => {
                    const idx = prev.findIndex(l => l.sku === j.sku);
                    if (idx >= 0) {
                        const next = [...prev];
                        next[idx] = { ...next[idx], scanned: j.line_scanned };
                        return next;
                    }
                    return [{ sku: j.sku, title: j.title, scanned: j.line_scanned }, ...prev];
                });
                setLastItem({ sku: j.sku, title: j.title });
                setLastFlash({ sku: j.sku, title: j.title });
                flashTimer.current = setTimeout(() => setLastFlash(null), 2500);
            } else {
                raiseScanAlert(`SKU not found — "${barcode}"`);
            }
        } catch (e) {
            raiseScanAlert(e.message || 'Network error');
        } finally {
            setScanning(false);
        }
    }, [scanInput, locationId, storeKey, scanning, ensureSession, invFetch]);

    // ── Set / Add a typed quantity for the item just scanned ──────────────
    const applyQty = useCallback(async (mode, n) => {
        if (!lastItem || qtyBusy || !locationId || !storeKey) return;
        const cur    = lines.find(l => l.sku === lastItem.sku)?.scanned || 0;
        const target = mode === 'add' ? cur + n : n;
        setQtyBusy(true);
        setScanError(null);
        try {
            const sid = await ensureSession();
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sid}/lines/${encodeURIComponent(lastItem.sku)}`
                + `?shopify_location_id=${encodeURIComponent(locationId)}`,
                { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ scanned: target, entry: mode }) },
            );
            const j = await r.json().catch(() => ({}));
            if (!r.ok) { raiseScanAlert(j.detail || 'Could not save the quantity'); return; }
            setLines(prev => (target === 0 ? prev.filter(l => l.sku !== lastItem.sku) : prev.map(l => l.sku === lastItem.sku ? { ...l, scanned: target } : l)));
            if (target === 0) setLastItem(null);
        } catch (e) {
            raiseScanAlert(e.message || 'Network error');
        } finally {
            setQtyBusy(false);
        }
    }, [lastItem, qtyBusy, locationId, storeKey, lines, ensureSession, invFetch]);

    // The scan box always has focus (scanners type into it); not while a picker / confirmation / notice is up.
    useScannerCapture(handleScan, screen === 'scan' && !!locationId && !showLocPicker && !alertMsg);

    // ── Undo handler ─────────────────────────────────────────────────────
    const handleUndo = useCallback(async (sku) => {
        if (!sessionId || !locationId || !storeKey) return;
        const r = await invFetch(
            `/api/inv/${storeKey}/sessions/${sessionId}/lines/${encodeURIComponent(sku)}/undo`
            + `?shopify_location_id=${encodeURIComponent(locationId)}`,
            { method: 'POST' },
        );
        if (!r.ok) return;
        const j = await r.json();
        setLines(prev =>
            j.line_scanned === 0
                ? prev.filter(l => l.sku !== sku)
                : prev.map(l => l.sku === sku ? { ...l, scanned: j.line_scanned } : l)
        );
    }, [sessionId, storeKey, locationId, invFetch]);

    // ── Review handler ────────────────────────────────────────────────────
    // v2: Review is a read-only preview (nothing is submitted or frozen, Back to scanning still works);
    // Apply is the one final step. Before v2, Review submitted the session.
    const handleSubmit = useCallback(async () => {
        if (!sessionId || !locationId || submitting) return;
        setSubmitting(true);
        setScanError(null);
        try {
            const r = WAREHOUSE_V2
                ? await invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/preview?shopify_location_id=${encodeURIComponent(locationId)}`)
                : await invFetch(
                    `/api/inv/${storeKey}/sessions/${sessionId}/submit`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ shopify_location_id: locationId }),
                    },
                );
            const j = await r.json();
            if (!r.ok) { setScanError(j.detail || 'Review failed'); return; }
            if (WAREHOUSE_V2) setReviewLines(j.lines || []); else setLines(j.lines || []);
            setScreen('review');
        } catch (e) {
            setScanError(e.message);
        } finally {
            setSubmitting(false);
        }
    }, [sessionId, storeKey, locationId, submitting, invFetch]);

    // ── Apply handler ─────────────────────────────────────────────────────
    const handleApply = useCallback(async () => {
        if (!sessionId || !locationId || applying) return;
        setApplying(true);
        setScanError(null);
        try {
            if (WAREHOUSE_V2) {
                // freeze the session just before applying; 409 = already frozen by an earlier attempt
                const rs = await invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/submit`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ shopify_location_id: locationId }),
                });
                if (!rs.ok && rs.status !== 409) {
                    const js = await rs.json().catch(() => ({}));
                    setScanError(js.detail || 'Could not finish the Add Inventory');
                    return;
                }
            }
            const r = await invFetch(
                `/api/inv/${storeKey}/sessions/${sessionId}/apply`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ shopify_location_id: locationId }),
                },
            );
            const j = await r.json();
            if (!r.ok || !j.ok) {
                setScanError(
                    j.errors?.map(e => `${e.sku}: ${e.error}`).join('; ')
                    || j.detail || 'Apply failed',
                );
                return;
            }
            setApplyResult(j);
            setScreen('applied');
        } catch (e) {
            setScanError(e.message);
        } finally {
            setApplying(false);
        }
    }, [sessionId, storeKey, locationId, applying, invFetch]);

    // v2: Add Inventory needs no approval — whoever scanned the stock adds it.
    const canApply = useMemo(
        () => WAREHOUSE_V2 || (consoleMode && hasRole(user, ['admin', 'site_manager'])),
        [consoleMode, user],
    );

    const totalUnits = useMemo(() => lines.reduce((s, l) => s + (l.scanned || 0), 0), [lines]);

    // ── Renders ───────────────────────────────────────────────────────────

    const renderHeader = (subtitle) => (
        <div style={S.bar}>
            <div style={S.barRow}>
                <div>
                    <div style={S.barTitle}>ADD INVENTORY</div>
                    <div style={S.barSub}>{locationName || subtitle || 'Select location'}</div>
                </div>
                {locationId && myAssignments.length > 1 && (
                    <button style={S.backBtn} onClick={() => setShowLocPicker(true)}>
                        📍 Change location
                    </button>
                )}
            </div>
            <button style={S.backBtn} onClick={() => navigate('../home', { relative: 'path' })}>
                ← Back to menu
            </button>
        </div>
    );

    // ── Scan screen ───────────────────────────────────────────────────────
    if (screen === 'scan') {
        return (
            <div style={S.shell}>
                {renderHeader()}
                {alertNode}
                <div style={S.body}>
                    {/* Barcode input */}
                    <div style={S.scanCard}>
                        <div style={S.scanLabel}>SCAN OR TYPE BARCODE / SKU</div>
                        <div style={S.scanRow}>
                            <input
                                ref={scanRef}
                                style={S.scanInput}
                                value={scanInput}
                                onChange={e => { setScanInput(e.target.value); setScanError(null); }}
                                onKeyDown={e => e.key === 'Enter' && handleScan()}
                                placeholder={lastItem ? 'Scan next item…' : 'Barcode or SKU…'}
                                inputMode="text"
                                autoComplete="off"
                                autoCapitalize="none"
                                spellCheck={false}
                                disabled={scanning || !!alertMsg || !locationId}
                            />
                            <button
                                style={{ ...S.scanBtn, ...(scanning ? S.scanBtnBusy : {}) }}
                                onClick={handleScan}
                                disabled={scanning || qtyBusy || !scanInput.trim() || !locationId}
                            >
                                {scanning ? '…' : 'Add'}
                            </button>
                        </div>
                    </div>

                    <InvQtyActions item={lastItem} count={lines.find(l => l.sku === lastItem?.sku)?.scanned || 0}
                                   busy={qtyBusy} onApply={applyQty} />

                    {/* Error */}
                    {scanError && <div style={S.errCard}>{scanError}</div>}

                    {/* Line list */}
                    {lines.length > 0 && (
                        <div style={S.lineSection}>
                            <div style={S.lineHdr}>Scanned items ({lines.length} SKU{lines.length !== 1 ? 's' : ''})</div>
                            {lines.map(line => (
                                <div key={line.sku} style={S.lineCard}>
                                    <div style={S.lineQty}>{line.scanned}</div>
                                    <div style={S.lineInfo}>
                                        <div style={S.lineSku}>{line.sku}</div>
                                        {line.title && <div style={S.lineTitle}>{line.title}</div>}
                                    </div>
                                    <button style={S.undoBtn} onClick={() => handleUndo(line.sku)} title="Undo last scan">
                                        undo
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {lines.length === 0 && locationId && !scanning && (
                        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af' }}>
                            <div style={{ fontSize: 40, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 600, color: '#6b7280' }}>Scan your first item</div>
                            <div style={{ fontSize: 13, marginTop: 4 }}>Scan or type a barcode above</div>
                        </div>
                    )}

                    {!locationId && (
                        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#9ca3af' }}>
                            <div style={{ fontSize: 13 }}>Select a location to begin</div>
                        </div>
                    )}
                </div>

                {/* Bottom bar */}
                <div style={S.bottomBar}>
                    <div style={S.countPill}>
                        {lines.length > 0
                            ? `${lines.length} SKU${lines.length !== 1 ? 's' : ''} · ${totalUnits} unit${totalUnits !== 1 ? 's' : ''}`
                            : 'No items yet'}
                    </div>
                    <button
                        style={{ ...S.submitBtn, ...(lines.length === 0 || submitting ? S.submitBtnDis : {}) }}
                        onClick={handleSubmit}
                        disabled={lines.length === 0 || submitting}
                    >
                        {submitting ? 'Submitting…' : 'Review →'}
                    </button>
                </div>

                {/* Location picker */}
                {showLocPicker && (
                    <div style={S.overlay} onClick={() => { if (locationId) setShowLocPicker(false); }}>
                        <div style={S.sheet} onClick={e => e.stopPropagation()}>
                            <div style={S.sheetTitle}>Select your location</div>
                            {myAssignments.map(a => (
                                <button key={a.shopify_location_id} style={S.locBtn} onClick={() => {
                                    setLocationId(a.shopify_location_id);
                                    setLocationName(a.location_name || '');
                                    setStoreKey(a.store_key);
                                    setSessionId(null);
                                    setLines([]); setLastItem(null); setFrozen(false);
                                    setScreen('scan');
                                    setShowLocPicker(false);
                                }}>
                                    {a.location_name || a.shopify_location_id}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ── Review screen ─────────────────────────────────────────────────────
    if (screen === 'review') {
        const rl = WAREHOUSE_V2 ? reviewLines : lines;
        const linesWithVariant = rl.filter(l => l.variant_gid);
        // v2: Add Inventory ADDS the scanned quantity to what is on hand; before v2 it replaced it like a count
        const changedLines = linesWithVariant.filter(l => {
            if (WAREHOUSE_V2) return (l.scanned || 0) > 0;
            const eff = (l.scanned || 0) + (l.picked_added || 0) + (l.overstock_counted || 0);
            return l.live_on_hand !== null && l.live_on_hand !== undefined && eff !== l.live_on_hand;
        });
        const totalAdding = linesWithVariant.reduce((n, l) => n + (l.scanned || 0), 0);
        return (
            <div style={S.shell}>
                {renderHeader(WAREHOUSE_V2 ? 'Review stock to add' : 'Review counts')}
                <div style={S.body}>
                    {scanError && <div style={{ ...S.errCard, margin: '12px 12px 0' }}>{scanError}</div>}

                    <div style={{ padding: '12px 16px 0', fontSize: 13, color: '#6b7280' }}>
                        {WAREHOUSE_V2
                            ? `${totalAdding} unit${totalAdding !== 1 ? 's' : ''} across ${changedLines.length} SKU${changedLines.length !== 1 ? 's' : ''} will be added to what is on hand.`
                            : `${changedLines.length} of ${linesWithVariant.length} SKU${linesWithVariant.length !== 1 ? 's' : ''} will be updated.`}
                        {' '}
                        {rl.filter(l => !l.variant_gid).length > 0 &&
                            `${rl.filter(l => !l.variant_gid).length} unresolved SKU${rl.filter(l => !l.variant_gid).length !== 1 ? 's' : ''} skipped.`
                        }
                    </div>

                    <div style={S.revCard}>
                        <table style={S.revTable}>
                            <thead>
                                <tr>
                                    <th style={S.revTh}>SKU</th>
                                    {WAREHOUSE_V2 ? (
                                        <>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Adding</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>On hand now</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>New total</th>
                                        </>
                                    ) : (
                                        <>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Counted</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Picked</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Effective</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Was</th>
                                            <th style={{ ...S.revTh, textAlign: 'right' }}>Δ</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {rl.map(line => {
                                    const eff   = (line.scanned || 0) + (line.picked_added || 0) + (line.overstock_counted || 0);
                                    const was   = line.live_on_hand;
                                    const delta = (was !== null && was !== undefined) ? eff - was : null;
                                    const dColor = delta === null ? '#9ca3af'
                                                 : delta > 0   ? '#065f46'
                                                 : delta < 0   ? '#991b1b'
                                                 : '#9ca3af';
                                    const bd = breakdown?.skus?.[line.sku];
                                    const isOpen = !!openSkus[line.sku];
                                    return (
                                        <React.Fragment key={line.sku}>
                                        <tr>
                                            <td style={{ ...S.revTd, fontWeight: 600, fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: bd ? 'pointer' : 'default' }}
                                                onClick={() => bd && setOpenSkus(o => ({ ...o, [line.sku]: !o[line.sku] }))}>
                                                {bd ? (isOpen ? '▾ ' : '▸ ') : ''}{line.sku}
                                                {line.bin_location && <div style={{ fontSize: 11, color: '#9ca3af' }}>bin {line.bin_location}</div>}
                                            </td>
                                            {WAREHOUSE_V2 ? (
                                                <>
                                                    <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700, color: '#065f46' }}>+{line.scanned || 0}</td>
                                                    <td style={{ ...S.revTd, textAlign: 'right', color: '#6b7280' }}>
                                                        {was !== null && was !== undefined ? was : '—'}
                                                    </td>
                                                    <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700 }}>
                                                        {was !== null && was !== undefined ? was + (line.scanned || 0) : '—'}
                                                    </td>
                                                </>
                                            ) : (
                                                <>
                                            <td style={{ ...S.revTd, textAlign: 'right' }}>{line.scanned || 0}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', color: '#6b7280' }}>{line.picked_added || 0}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700 }}>{eff}</td>
                                            <td style={{ ...S.revTd, textAlign: 'right', color: '#6b7280' }}>
                                                {was !== null && was !== undefined ? was : '—'}
                                            </td>
                                            <td style={{ ...S.revTd, textAlign: 'right', fontWeight: 700, color: dColor }}>
                                                {delta === null ? '—'
                                                 : delta === 0  ? '='
                                                 : delta > 0    ? `+${delta}`
                                                 : delta}
                                            </td>
                                                </>
                                            )}
                                        </tr>
                                        {isOpen && bd && (
                                            <tr>
                                                <td colSpan={6} style={{ ...S.revTd, background: '#f9fafb', fontSize: 12 }}>
                                                    {bd.users.map(u => (
                                                        <div key={u.user} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '2px 0' }}>
                                                            <span>{u.user}</span>
                                                            <span>
                                                                {u.scanned > 0 && <>Scanned <b>{u.scanned}</b></>}
                                                                {u.scanned > 0 && u.entered !== 0 && ' · '}
                                                                {u.entered !== 0 && <>Entered <b>{u.entered > 0 ? `+${u.entered}` : u.entered}</b></>}
                                                            </span>
                                                        </div>
                                                    ))}
                                                    {bd.adjustment !== 0 && (
                                                        <div style={{ padding: '2px 0', color: '#92400e' }}>
                                                            Entered by someone (not attributed) / removed scans: {bd.adjustment > 0 ? `+${bd.adjustment}` : bd.adjustment}
                                                        </div>
                                                    )}
                                                    {bd.users.length === 0 && bd.adjustment === 0 && <div style={{ color: '#6b7280' }}>No scans recorded</div>}
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 12, padding: '16px 12px', flexWrap: 'wrap' }}>
                        <button style={{ ...S.undoBtn, padding: '11px 18px', fontSize: 14 }} disabled={!breakdown}
                                onClick={() => exportReviewCsv(rl, breakdown, `add-inventory-${sessionId}-review.csv`, null, { adding: WAREHOUSE_V2 })}>
                            Export CSV
                        </button>
                        <button
                            style={{ ...S.undoBtn, padding: '11px 18px', fontSize: 14 }}
                            onClick={async () => {
                                // A session that was already submitted is frozen: reopen it so scanning works again (v2)
                                if (WAREHOUSE_V2 && frozen) {
                                    try {
                                        const rr = await invFetch(`/api/inv/${storeKey}/sessions/${sessionId}/reopen`, {
                                            method: 'POST',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ shopify_location_id: locationId }),
                                        });
                                        if (!rr.ok) { const jr = await rr.json().catch(() => ({})); setScanError(jr.detail || 'Could not reopen'); return; }
                                        setFrozen(false);
                                    } catch (e) { setScanError(e.message); return; }
                                }
                                setScreen('scan');
                            }}
                        >
                            ← Back to scanning
                        </button>
                        {canApply ? (
                            <button
                                style={{ ...S.applyBtn, ...(applying ? S.applyBtnDis : {}), flex: 1 }}
                                onClick={handleApply}
                                disabled={applying || changedLines.length === 0}
                            >
                                {applying ? (WAREHOUSE_V2 ? 'Adding…' : 'Applying…') : (WAREHOUSE_V2 ? `Add ${totalAdding} unit${totalAdding !== 1 ? 's' : ''} to inventory` : `Apply ${changedLines.length} change${changedLines.length !== 1 ? 's' : ''}`)}
                            </button>
                        ) : (
                            <div style={{ flex: 1, background: '#f0f9ff', border: '1px solid #bfdbfe',
                                          borderRadius: 8, padding: '11px 14px', fontSize: 13, color: '#1d4ed8' }}>
                                ℹ️ Submitted — awaiting manager approval to apply
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── Applied screen ────────────────────────────────────────────────────
    if (screen === 'applied') {
        const changed = (applyResult?.results || []).filter(r => r.ok).length;
        const skipped = (applyResult?.results || []).filter(r => r.skipped).length;
        return (
            <div style={S.shell}>
                {renderHeader('Applied')}
                <div style={S.body}>
                    <div style={S.successWrap}>
                        <div style={S.successIcon}>✅</div>
                        <div style={S.successH}>{WAREHOUSE_V2 ? 'Stock added' : 'Inventory updated'}</div>
                        <div style={S.successSub}>
                            {changed} SKU{changed !== 1 ? 's' : ''} updated
                            {skipped > 0 ? `, ${skipped} unchanged` : ''}.
                        </div>
                        <button style={S.doneBtn} onClick={() => navigate('../home', { relative: 'path' })}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
